import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Connects to data-controller="event-tags"
// The merged "Category & tags" card: both AI-generated (no free text). Renders
// removable tag pills from the hidden inputs the form submits. The single
// Regenerate re-classifies the category AND regenerates tags server-side in
// one call — the explicit click overwrites both; background autofill never does.
export default class extends Controller {
  static targets = ["tagList", "hiddenContainer", "regenerate", "regenerateLabel", "categorySelect"]
  static values = { empty: String }

  connect() {
    this.renderPills()
  }

  async regenerate() {
    const title = this.readFormField("event[title]")
    const about = this.currentAboutHTML()

    this.setLoading(true)
    try {
      const response = await patch("/organizer/events/generate_ai_tags", {
        contentType: "application/json",
        body: { event_title: title, current_about: about },
        responseKind: "json",
      })
      if (response.ok) {
        const data = await response.json
        this.setTags(Array.isArray(data.tags) ? data.tags : [])
        if (data.category_id && this.hasCategorySelectTarget) {
          this.categorySelectTarget.value = String(data.category_id)
        }
      }
    } catch (e) {
      // best-effort — leave existing tags in place on failure
    } finally {
      this.setLoading(false)
    }
  }

  // Replace the whole tag set (hidden inputs + pills) with a fresh list.
  setTags(names) {
    this.hiddenContainerTarget.innerHTML = ""
    names.forEach((name) => this.appendHidden(name))
    this.renderPills()
  }

  remove(e) {
    const pill = e.target.closest(".tag-item")
    if (!pill) return
    const pills = Array.from(this.tagListTarget.children)
    const index = pills.indexOf(pill)
    if (index === -1) return
    const inputs = this.hiddenContainerTarget.querySelectorAll("input[name='event[tag_names][]']")
    if (inputs[index]) inputs[index].remove()
    pill.remove()
  }

  appendHidden(name) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "event[tag_names][]"
    input.value = name
    this.hiddenContainerTarget.appendChild(input)
  }

  renderPills() {
    this.tagListTarget.innerHTML = ""
    const inputs = this.hiddenContainerTarget.querySelectorAll("input[name='event[tag_names][]']")
    if (inputs.length === 0 && this.emptyValue) {
      const note = document.createElement("span")
      note.className = "text-muted small"
      note.textContent = this.emptyValue
      this.tagListTarget.appendChild(note)
      return
    }
    inputs.forEach((input) => {
      if (input.value) this.appendPill(input.value)
    })
  }

  appendPill(name) {
    const pill = document.createElement("span")
    pill.className = "tag-item gap-1"
    pill.innerHTML = `${this.escapeHtml(name)} <button type="button" class="btn remove-tag-btn" data-action="click->event-tags#remove" aria-label="Remove">×</button>`
    this.tagListTarget.appendChild(pill)
  }

  setLoading(on) {
    if (this.hasRegenerateTarget) this.regenerateTarget.disabled = on
    if (this.hasRegenerateLabelTarget && on) this.regenerateLabelTarget.dataset._t = this.regenerateLabelTarget.textContent
    if (this.hasRegenerateLabelTarget) {
      this.regenerateLabelTarget.textContent = on ? "…" : (this.regenerateLabelTarget.dataset._t || this.regenerateLabelTarget.textContent)
    }
  }

  // Read a plain form field's value by name.
  readFormField(name) {
    const el = this.element.closest("form")?.querySelector(`[name="${name}"]`)
    return el ? el.value : ""
  }

  // Trix stores the about HTML in a hidden input; fall back to empty.
  currentAboutHTML() {
    const el = this.element.closest("form")?.querySelector('input[name="event[about]"], trix-editor')
    if (!el) return ""
    return el.value || el.innerHTML || ""
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
